import { EventType } from "@/analytics/analytics-events";
import type {
  CancelAnnotationCreationEventProperties,
  SaveAnnotationEventProperties,
} from "@/analytics/annotation-events-properties";
import type { OpenTagManagementProperties } from "@/analytics/tag-events-properties";
import { useErrorHandlers } from "@/errors/components/error-handling-context";
import { selectAnnotationAssignableUsers } from "@/store/project-selector";
import { useAppSelector } from "@/store/store-hooks";
import type { Tag } from "@/store/tags/tags-slice";
import { selectCurrentUser } from "@/store/user-selectors";
import {
  selectMarkupsDisplayForViewers,
  STATUS_OPTIONS,
} from "@faro-lotv/app-component-toolbox";
import { Analytics } from "@faro-lotv/foreign-observers";
import { isEqual } from "es-toolkit";
import { useCallback, useMemo, useState, type JSX } from "react";
import { TagsManagementDialog } from "../tags/tags-management-dialog";
import { useProjectTagOptions } from "../tags/use-project-tag-options";
import { UserTeamAvatar } from "../users-invite/user-team-avater";
import {
  AnnotationEditor,
  type AnnotationEditorProps,
  type AnnotationTypeDropdownProps,
} from "./annotation-editor/annotation-editor";
import { CreateAnnotationFormProps } from "./create-annotation-form";
import { useAnnotationAttachments } from "./use-annotation-attachments";
import { useAnnotationUploadAttachments } from "./use-annotation-upload-attachments";
import { useIsAnnotationEditorDark } from "./annotation-editor/annotation-editor-surface-context";

/**
 * @returns an annotation dialog that is specific to Sphere XG
 */
export function SphereAnnotation({
  children,
  onSave,
  onClose,
  showAnnotationTypeDropdown,
  selectedAnnotationType,
  onAnnotationTypeChange,
  annotationTypeOptions,
  initialTitle,
  initialDescription,
  initialAttachment,
}: React.PropsWithChildren<
  CreateAnnotationFormProps &
    AnnotationTypeDropdownProps &
    Pick<AnnotationEditorProps, "showAnnotationTypeDropdown">
>): JSX.Element {
  const isDark = useIsAnnotationEditorDark();
  const [title, setTitle] = useState(initialTitle ?? "");
  const [description, setDescription] = useState<string | undefined>(
    initialDescription,
  );
  const [assignee, setAssignee] = useState<string>();
  const [status, setStatus] = useState<string>();
  const [dueDate, setDueDate] = useState<Date>();
  const [isSaving, setIsSaving] = useState(false);
  const [isTagsManagementOpen, setIsTagsManagementOpen] = useState(false);
  const [selectedTags, setSelectedTags] = useState<Tag[]>([]);

  const currentUser = useAppSelector(selectCurrentUser);
  const allowViewers = useAppSelector(selectMarkupsDisplayForViewers);
  const projectUsers = useAppSelector(
    selectAnnotationAssignableUsers(!!allowViewers),
    isEqual,
  );

  // Create a list with useMemo to avoid recreating one at each frame and recomputing the useAnnotationAttachments hook
  const initialAttachments = useMemo(() => [], []);

  const { attachments, addNewAttachment } = useAnnotationAttachments(
    initialAttachments,
    "Create",
    initialAttachment,
  );

  const { tagOptions, isProcessing } = useProjectTagOptions({
    dark: isDark,
    onTagRemoved: (tag) => {
      // Remove the deleted tag from the selected tags
      setSelectedTags((prev: Tag[]) => prev.filter((t) => t.id !== tag.id));
    },
  });

  const { handleErrorWithToast } = useErrorHandlers();
  const { uploadAttachments, progress } = useAnnotationUploadAttachments();

  const createAnnotation = useCallback(() => {
    Analytics.track<SaveAnnotationEventProperties>(EventType.saveAnnotation, {
      isNew: true,
      statusSet: !!status,
      assigneeSet: !!assignee,
      assignToCurrentUser: !!currentUser && assignee === currentUser.id,
      dateSet: !!dueDate,
      descriptionSet: !!description,
      numberOfAttachmentsAdded: attachments.length,
      tagsAdded: selectedTags.length,
      tagsRemoved: 0,
    });

    setIsSaving(true);

    uploadAttachments(attachments)
      .then((newAttachments) =>
        onSave({
          title,
          assignee,
          description,
          dueDate,
          status,
          newAttachments,
          tags: selectedTags,
        }),
      )
      .catch((error) => {
        handleErrorWithToast({ title: "Saving annotation failed", error });
      })
      .finally(() => {
        setIsSaving(false);
      });
  }, [
    status,
    assignee,
    currentUser,
    dueDate,
    description,
    attachments,
    uploadAttachments,
    onSave,
    title,
    selectedTags,
    handleErrorWithToast,
  ]);

  return (
    <>
      <AnnotationEditor
        onDescriptionError={(error) =>
          handleErrorWithToast({ title: "Error in description", error })
        }
        progress={progress}
        disabled={isSaving || isProcessing}
        showConfirmButtonSpinner={isSaving}
        title={title}
        initialDescription={initialDescription}
        assignee={assignee}
        status={status}
        selectedAnnotationType={selectedAnnotationType}
        attachments={attachments}
        tags={selectedTags}
        addNewAttachment={addNewAttachment}
        onTagsChange={setSelectedTags}
        onTitleChange={setTitle}
        onDescriptionChange={setDescription}
        onAssigneeChange={setAssignee}
        onStatusChange={(e) => setStatus(e.target.value)}
        onAnnotationTypeChange={onAnnotationTypeChange}
        onDueDateChange={(e) => setDueDate(e)}
        assigneeOptions={projectUsers.map((user) => ({
          key: user.id ?? user.email,
          value: user.id ?? user.email,
          label: (
            <UserTeamAvatar
              userDisplayInfo={user}
              size="xs"
              shouldShowWhiteRim={false}
              dark={isDark}
              displayOption="nameAndEmail"
              isTeam={user.authorityType === "UserGroupRef"}
            />
          ),
        }))}
        statusOptions={STATUS_OPTIONS}
        annotationTypeOptions={annotationTypeOptions}
        tagOptions={tagOptions}
        confirmButtonText={isSaving ? "Creating" : "Create"}
        shouldShowAttachments
        showAnnotationTypeDropdown={showAnnotationTypeDropdown}
        autoFocusTitle
        onManageTagsClick={() => {
          Analytics.track<OpenTagManagementProperties>(
            EventType.openTagManagement,
            { via: "annotation" },
          );
          setIsTagsManagementOpen(true);
        }}
        onCancelButtonClick={() => {
          Analytics.track<CancelAnnotationCreationEventProperties>(
            EventType.cancelAnnotationCreation,
            { via: "dialog" },
          );
          onClose();
        }}
        onConfirmButtonClick={createAnnotation}
      >
        {children}
      </AnnotationEditor>
      <TagsManagementDialog
        open={isTagsManagementOpen}
        onClose={() => setIsTagsManagementOpen(false)}
      />
    </>
  );
}
